/**
 * Vocabulário de saúde de endereço de e-mail.
 *
 * Um bounce cru do SendGrid é uma string do servidor remoto ("550 5.1.1 The
 * email account that you tried to reach does not exist", "Domain has no MX
 * record") — útil para depurar UM caso, inútil para decidir o que fazer com
 * 450. Este módulo traduz o par (tipo de evento, motivo) em uma categoria
 * estável, e é a categoria que carrega a decisão: suprime ou não suprime.
 *
 * Fica em shared/ porque servidor e tela precisam concordar. O servidor
 * classifica ao gravar a supressão; a tela agrupa, rotula e explica ao usuário
 * usando exatamente as mesmas chaves.
 */

export const EMAIL_HYGIENE_REASONS = [
  "hard_bounce",
  "invalid_domain",
  "previous_bounce",
  "spam_report",
  "spam_rejected",
  "unsubscribed",
  "invalid_address",
  "mailbox_full",
  "blocked",
  "other",
] as const;

export type EmailHygieneReason = (typeof EMAIL_HYGIENE_REASONS)[number];

export interface EmailHygieneReasonDef {
  key: EmailHygieneReason;
  /** Rótulo curto, para chip de tabela e card de resumo. */
  label: string;
  /** Uma frase explicando ao usuário o que aconteceu com aquele endereço. */
  description: string;
  /**
   * Se `true`, um endereço nesta categoria para de receber e-mail assim que o
   * evento chega — sem intervenção. São os casos onde reenviar não tem chance
   * de dar certo E queima reputação de domínio.
   *
   * `false` NÃO significa "ignore": significa que é transitório ou externo ao
   * endereço, e suprimir seria jogar fora um contato válido. Caixa cheia
   * esvazia; bloqueio por IP na blacklist é problema do provedor de envio, não
   * do destinatário.
   */
  suppresses: boolean;
  /** O que o usuário deve fazer. Aparece no card de resumo da higiene. */
  advice: string;
}

export const EMAIL_HYGIENE_REASON_DEFS: Record<EmailHygieneReason, EmailHygieneReasonDef> = {
  hard_bounce: {
    key: "hard_bounce",
    label: "Conta inexistente",
    description: "O servidor respondeu que esse endereço não existe.",
    suppresses: true,
    advice: "Não tem volta. Mantenha suprimido ou exclua o contato.",
  },
  invalid_domain: {
    key: "invalid_domain",
    label: "Domínio sem e-mail",
    description: "O domínio não tem servidor de e-mail configurado — costuma ser órgão que trocou de domínio ou desativou o antigo.",
    suppresses: true,
    advice: "Procure o endereço novo do órgão antes de excluir.",
  },
  previous_bounce: {
    key: "previous_bounce",
    label: "Já retornou antes",
    description: "O endereço já tinha retornado em envio anterior e foi barrado antes de sair.",
    suppresses: true,
    advice: "Já está fora dos envios. Exclua para tirar da contagem da base.",
  },
  spam_report: {
    key: "spam_report",
    label: "Marcou como spam",
    description: "O destinatário denunciou a mensagem como spam.",
    suppresses: true,
    advice: "Nunca reenviar. Reenviar é o pior movimento para a reputação.",
  },
  spam_rejected: {
    key: "spam_rejected",
    label: "Recusado como spam",
    description: "O servidor do destinatário recusou a mensagem por suspeita de spam — o endereço pode ser válido.",
    suppresses: false,
    advice: "Problema de autenticação ou reputação do remetente, não do contato. Revise SPF/DKIM/DMARC.",
  },
  unsubscribed: {
    key: "unsubscribed",
    label: "Descadastrado",
    description: "O contato pediu para sair da lista.",
    suppresses: true,
    advice: "Obrigatório manter fora. Não reative sem novo consentimento.",
  },
  invalid_address: {
    key: "invalid_address",
    label: "Endereço com erro de digitação",
    description: "O endereço está malformado (domínio escrito errado, ponto duplicado).",
    suppresses: true,
    advice: "Vale corrigir à mão: geralmente é um contato real com um typo.",
  },
  mailbox_full: {
    key: "mailbox_full",
    label: "Caixa cheia",
    description: "A caixa do destinatário está sem espaço. É temporário.",
    suppresses: false,
    advice: "Não excluir. O endereço volta a receber quando a caixa esvaziar.",
  },
  blocked: {
    key: "blocked",
    label: "Bloqueio do provedor",
    description: "O servidor recusou por bloqueio de IP ou reputação do remetente.",
    suppresses: false,
    advice: "Não é o contato. Acompanhe a reputação dos IPs de envio.",
  },
  other: {
    key: "other",
    label: "Outro motivo",
    description: "Falha que não se encaixa nas categorias conhecidas.",
    suppresses: false,
    advice: "Confira o motivo original antes de decidir.",
  },
};

/** Categorias que tiram o endereço dos envios automaticamente. */
export const SUPPRESSING_REASONS: EmailHygieneReason[] = EMAIL_HYGIENE_REASONS
  .filter((r) => EMAIL_HYGIENE_REASON_DEFS[r].suppresses);

/**
 * Supressões que NENHUM operador pode desfazer.
 *
 * As demais categorias descrevem um defeito técnico do endereço — domínio que
 * caiu, caixa que sumiu — e é legítimo alguém dizer "esse voltou a funcionar,
 * pode enviar". Estas duas descrevem a VONTADE do destinatário: ele pediu para
 * sair, ou denunciou a mensagem como spam. Consentimento não é um estado que a
 * operação revoga por conta própria (LGPD art. 8º §5º / CAN-SPAM), e um clique
 * errado numa tela de higiene não pode religar o envio para quem se descadastrou.
 *
 * Concretamente, isso governa três pontos:
 *  - `reactivateEmailAddresses` recusa estas linhas e informa quantas pulou.
 *  - `recordEmailSuppression` grava-as por cima de qualquer decisão manual.
 *  - `getSuppressedContactIds` ignora a reativação quando o motivo é este.
 *
 * Voltar a enviar para alguém daqui exige consentimento novo — ou seja, um
 * cadastro novo, não um botão.
 */
export const IRREVERSIBLE_REASONS: EmailHygieneReason[] = ["unsubscribed", "spam_report"];

export function isIrreversibleReason(reason: string): boolean {
  return (IRREVERSIBLE_REASONS as string[]).includes(reason);
}

export function isEmailHygieneReason(value: unknown): value is EmailHygieneReason {
  return typeof value === "string" && (EMAIL_HYGIENE_REASONS as readonly string[]).includes(value);
}

/**
 * Traduz um evento de falha do SendGrid em categoria.
 *
 * A ordem dos testes importa: o texto do motivo é mais específico que o tipo
 * do evento, então quem casa por texto decide primeiro. Um `dropped` com
 * "Bounced Address" é `previous_bounce`, não um dropped genérico; um `bounce`
 * cujo texto fala de MX é `invalid_domain`, não `hard_bounce` — a diferença
 * importa porque o conselho ao usuário é outro (procurar o domínio novo do
 * órgão vs. jogar fora).
 *
 * @param eventType  Tipo do evento (`bounce`, `dropped`, `spamreport`, …)
 * @param bounceType `metadata.type` do SendGrid: 'bounce' = hard, 'blocked' = soft
 * @param reason     `metadata.reason` — resposta crua do servidor remoto
 */
export function classifyEmailFailure(
  eventType: string,
  bounceType: string | null | undefined,
  reason: string | null | undefined,
): EmailHygieneReason {
  const text = (reason ?? "").toLowerCase();

  if (eventType === "spamreport") return "spam_report";
  if (eventType === "unsubscribe" || eventType === "group_unsubscribe") return "unsubscribed";

  // "Bounced Address" é como o SendGrid rotula o drop de endereço que já está
  // na lista de supressão dele. É a categoria que se repetia em toda edição da
  // newsletter sem nunca ser suprimida do nosso lado.
  if (text.includes("bounced address") || text.includes("previously bounced")) return "previous_bounce";
  if (text.includes("unsubscribed address") || text.includes("global unsubscribe")) return "unsubscribed";
  if (text.includes("spam") && (text.includes("5.7.1") || text.includes("rejected") || text.includes("blocked as spam"))) {
    return "spam_rejected";
  }

  // Caixa cheia: 552 (limite excedido) e o texto da Microsoft, que não usa o
  // código padrão. Transitório — testado antes de qualquer regra de hard bounce.
  if (text.includes("quota") || text.includes("mailbox is full") || text.includes("over quota")
    || text.includes("insufficient system storage") || text.includes("5.2.2") || text.includes("4.2.2")) {
    return "mailbox_full";
  }

  if (text.includes("no mx") || text.includes("mx record") || text.includes("unable to resolve")
    || text.includes("domain not found") || text.includes("nxdomain") || text.includes("no such domain")
    || text.includes("ptr")) {
    return "invalid_domain";
  }

  if (text.includes("syntax") || text.includes("malformed") || text.includes("invalid address")
    || text.includes("5.1.3")) {
    return "invalid_address";
  }

  if (text.includes("does not exist") || text.includes("no such user") || text.includes("user unknown")
    || text.includes("unknown user") || text.includes("recipient rejected")
    || text.includes("mailbox unavailable") || text.includes("5.1.1")) {
    return "hard_bounce";
  }

  // Sem texto reconhecível, o tipo do SendGrid decide: 'bounce' é permanente,
  // 'blocked' é transitório. `dropped` sem motivo casável fica em `other` — não
  // dá para afirmar que o endereço é ruim.
  if (bounceType === "bounce") return "hard_bounce";
  if (bounceType === "blocked") return "blocked";
  if (eventType === "bounce") return "hard_bounce";

  return "other";
}
